import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  DateRangeWrapper,
  DateRangeContainer,
  DateRangeContent,
} from './shared.styles';
import DateRangeDialog from './DateRangeDialog';
import moment from 'moment';
import DateRangeHeader from './DateRangeHeader';
import DateRangeFooter from './DateRangeFooter';
import useMedia from 'hooks/useMedia';
import PeriodSwitcher, { TSwitcherPeriod } from './PeriodSwitcher';
import { DateRangeContext, IDateRangeContext } from 'context/dateRangeContext';

interface IDateRangeProps {
  startDate?: Date;
  endDate?: Date;
  onChange: Function;
  minWeeklyDate?: moment.Moment;
  minDailyDate?: moment.Moment;
  minDate?: moment.Moment;
  maxDate?: moment.Moment;
  maxWeeklyDate?: moment.Moment;
  maxDailyDate?: moment.Moment;
  onCloseCalendar: () => void;
  margin: { top: number } | null;
  datePeriod?: TSwitcherPeriod;
  withPeriod?: boolean;
}

const noop = (): void => {}; // eslint-disable-line @typescript-eslint/no-empty-function

const DateRange: React.FC<IDateRangeProps> = (props) => {
  const {
    startDate = null,
    endDate = null,
    onChange,
    minDailyDate = moment('2010-01-01'), // min date in DF db
    minWeeklyDate = moment('2010-01-01'), // min date in DF db
    minDate = moment('2010-01-01'), // min date in DF db
    maxWeeklyDate = new Date(),
    maxDailyDate = new Date(),
    maxDate = new Date(),
    onCloseCalendar,
    margin,
    datePeriod = 'week',
    withPeriod,
  } = props;

  const isSmallScreen = useMedia('(max-width: 40em)');

  const [isVisible, setIsVisible] = useState(false);
  const [rangeType, setRangeType] = useState(datePeriod);
  const [activeFocus, setActiveFocus] = useState<'from' | 'to' | null>('from');

  const minDateValue = useMemo(() => {
    return withPeriod
      ? rangeType === 'day'
        ? minDailyDate
        : minWeeklyDate
      : minDate;
  }, [withPeriod, rangeType, minDailyDate, minWeeklyDate, minDate]);

  const maxDateValue = useMemo(() => {
    return withPeriod
      ? rangeType === 'day'
        ? maxDailyDate
        : maxWeeklyDate
      : maxDate;
  }, [withPeriod, rangeType, maxDailyDate, maxWeeklyDate, maxDate]);

  const lastAvailableDate = maxDateValue ? moment(maxDateValue) : moment();

  const maxAvailableDate = useMemo(() => {
    if (withPeriod && rangeType === 'day') return moment(maxDailyDate);

    const lastAvailableDateIsFriday = moment(lastAvailableDate).weekday() === 5;
    return lastAvailableDateIsFriday && lastAvailableDate === moment()
      ? moment(lastAvailableDate).subtract(1, 'days')
      : lastAvailableDate;
  }, [lastAvailableDate]);

  const defaultRange = useMemo(() => {
    return {
      from: moment(minDateValue).isAfter(
        moment(lastAvailableDate).subtract(1, 'months'),
        'day'
      )
        ? moment(minDateValue)
        : moment(lastAvailableDate).subtract(1, 'months'),
      to: maxAvailableDate,
    };
  }, [lastAvailableDate, minDateValue]);

  const [fromDate, setFromDate] = useState<moment.Moment | undefined>(
    startDate ? moment(startDate) : defaultRange.from
  );
  const [toDate, setToDate] = useState<moment.Moment | undefined>(
    endDate ? moment(endDate) : defaultRange.to
  );
  const [hoverDate, setHoverDate] = useState<moment.Moment | null>(null);

  const containerRef = useRef<any>(null);

  const onHoverDate = (date: moment.Moment): void => {
    setHoverDate(date);
  };

  const onSelectDate = (date: moment.Moment): void => {
    if (activeFocus) {
      if (
        activeFocus === 'from' ||
        (fromDate && date.isBefore(fromDate, 'date'))
      ) {
        setFromDate(date);
        if (toDate && date.isAfter(toDate, 'date')) {
          setToDate(undefined);
        }
        setActiveFocus('to');
      } else {
        setToDate(date);
        setActiveFocus(null);
      }
    } else {
      setFromDate(date);
      setActiveFocus('to');
      if (toDate && date.isAfter(toDate, 'date')) {
        setToDate(undefined);
      }
    }
  };

  function onSaveHandler(): void {
    onChange(
      moment.utc(fromDate).valueOf(),
      moment.utc(toDate).valueOf(),
      rangeType
    );
    onCloseCalendar();
  }

  const [needToScroll, setNeedToScroll] = useState<boolean>(false);

  function onResetHandler(): void {
    setFromDate(defaultRange.from);
    setToDate(defaultRange.to);
    onChange(
      moment(defaultRange.from).valueOf(),
      moment(defaultRange.to).valueOf(),
      'week'
    );

    if (rangeType !== 'week') {
      setRangeType('week');
    } else {
      setNeedToScroll(true);
    }
  }

  function onClearHandler(): void {
    setFromDate(undefined);
    setToDate(undefined);
    setActiveFocus(null);
  }

  useEffect(() => {
    if (rangeType === 'day') {
      if (fromDate && toDate && toDate.diff(fromDate, 'days') > 30) {
        onClearHandler();
      }
    }
    setNeedToScroll(true);
  }, [rangeType]);

  useEffect(() => {
    setIsVisible(true);
    const body = document.getElementById('root');
    body?.addEventListener('click', handleDocumentClick);

    return (): void => {
      setIsVisible(false);
      body?.removeEventListener('click', handleDocumentClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleDocumentClick(event: MouseEvent): void {
    event.stopPropagation();
    const node = event.target as Node;
    const target = event?.target as Element;

    if (
      containerRef &&
      containerRef.current &&
      containerRef.current.contains(node) === false &&
      target.className !== 'text-day' &&
      !target.hasAttribute('data-day-index')
    ) {
      onCloseCalendar();
    }
  }

  const contextValue: IDateRangeContext = useMemo(() => {
    return {
      datePeriod: rangeType,
      withPeriod,
      onHoverDate: isSmallScreen ? noop : onHoverDate,
      onSelectDate,
      minDate: moment(minDateValue),
      maxDate: maxAvailableDate,
      hoverDate: isSmallScreen ? null : hoverDate,
    };
  }, [
    hoverDate,
    minDateValue,
    maxDateValue,
    onSelectDate,
    onHoverDate,
    isSmallScreen,
    withPeriod,
    rangeType,
  ]);

  return (
    <DateRangeContext.Provider value={contextValue}>
      <DateRangeWrapper
        ref={containerRef}
        margins={margin}
        isSmallScreen={isSmallScreen}
      >
        <DateRangeContent isSmallScreen={isSmallScreen}>
          {withPeriod && (
            <PeriodSwitcher rangeType={rangeType} setRangeType={setRangeType} />
          )}
          <DateRangeHeader startDate={fromDate} endDate={toDate} />
          <DateRangeContainer>
            <DateRangeDialog
              minDate={moment(minDateValue)}
              maxDate={maxAvailableDate}
              fromDate={fromDate}
              toDate={toDate}
              startWeekDay={'sunday'}
              isVisible={isVisible}
              datePeriod={rangeType}
              needToScrollToEnd={needToScroll}
              setNeedToScroll={setNeedToScroll}
            />
          </DateRangeContainer>
          <DateRangeFooter
            canSave={!!fromDate && !!toDate}
            onReset={onResetHandler}
            onSave={onSaveHandler}
            onClose={onCloseCalendar}
            isSmallScreen={isSmallScreen}
            firstAvailableDate={minDateValue}
            onClear={onClearHandler}
            withPeriod={withPeriod}
          />
        </DateRangeContent>
      </DateRangeWrapper>
    </DateRangeContext.Provider>
  );
};

export default DateRange;
